import { assert, isNone, v2_fetchCmsPage } from '@crocodille/common';
import { AppPage } from '@framework/page';
import PageMeta from '@framework/page-meta';
import { getAppLayoutWithCart } from '@layouts/app-layout/app-layout';
import { getApp } from '@utils/app';
import { getServerSideTranslationProps } from '@utils/i18n';
import { GetStaticPaths, GetStaticProps } from 'next';
import Head from 'next/head';
import { CmsPageContent } from '../../../components/cms-page-content/cms-page-content';
import * as routing from '../../../routing';

interface Props {
  readonly cmsPage: NonNullable<Awaited<ReturnType<typeof v2_fetchCmsPage>>>;
  readonly headless?: boolean;
}

const Page: AppPage<Props> = ({ cmsPage }) => {
  return (
    <>
      <PageMeta
        pageTitle={cmsPage.title ?? ''}
        description={cmsPage.metaDescription ?? ''}
      />
      <Head>
        {cmsPage.metaDescription && (
          <meta name="description" content={cmsPage.metaDescription} />
        )}
      </Head>
      <CmsPageContent cmsPage={cmsPage} />
    </>
  );
};

Page.getLayout = (page, props) => {
  return props.headless ? page : getAppLayoutWithCart(page);
};

export const getStaticPaths: GetStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking',
  };
};

export const getStaticProps: GetStaticProps<Props> = async (ctx) => {
  const app = getApp(ctx);

  const slug = ctx.params?.page;

  assert(typeof slug === 'string', 'Slug must be a string');

  const [translationProps, cmsPage] = await Promise.all([
    getServerSideTranslationProps(app, ['messages']),
    v2_fetchCmsPage(app, slug),
  ]);

  if (isNone(cmsPage)) {
    return {
      notFound: true,
    };
  }

  if (cmsPage.slug !== slug) {
    return {
      redirect: {
        permanent: false,
        destination: routing.localized(
          ctx.locale,
          routing.cmsPages({
            page: cmsPage.slug,
          }),
        ),
      },
    };
  }

  return {
    props: {
      ...translationProps,
      cmsPage,
    },
  };
};

export default Page;
